/**
 * The report app's REPORTER TOKEN — one random id per install (or per
 * browser on the web), sent with every report and every event signup.
 *
 * It names nobody. It exists so that when somebody later makes an
 * account, the reports and signups this device already sent can be
 * claimed by it. Nothing recorded before the token shipped can ever be
 * claimed, which is why it ships ahead of accounts rather than with
 * them.
 *
 * Deliberately NOT the push `device_id`: that one is minted by the
 * server only once somebody allows notifications, so most reporters —
 * and every one on the web — never have one.
 *
 * Mirrored to Capacitor Preferences for the same reason the device id
 * is: the iOS WebView evicts localStorage under storage pressure, and
 * a token that quietly changes is a history nobody can claim.
 */

const STORAGE_KEY = 'tiq_report_reporter_token';

// Shape the server accepts. Anything else is re-minted rather than sent.
const TOKEN_PATTERN = /^[A-Za-z0-9-]{16,64}$/;

function preferences() {
    if (typeof window === 'undefined') return null;
    return window.Capacitor?.Plugins?.Preferences || null;
}

function mint() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    const rand = () => Math.random().toString(36).slice(2, 12);
    return `rt-${Date.now().toString(36)}-${rand()}${rand()}`;
}

function readLocal() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v && TOKEN_PATTERN.test(v) ? v : null;
    } catch { return null; }
}

function writeLocal(v) {
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* private mode */ }
}

async function readPrefs() {
    const P = preferences();
    if (!P) return null;
    try {
        const r = await P.get({ key: STORAGE_KEY });
        const v = r && typeof r.value === 'string' ? r.value : null;
        return v && TOKEN_PATTERN.test(v) ? v : null;
    } catch { return null; }
}

function writePrefs(v) {
    const P = preferences();
    if (!P) return;
    try { P.set({ key: STORAGE_KEY, value: v }); } catch { /* ignore */ }
}

let pending = null;

/**
 * The token, minted and persisted on first use. Asks Preferences
 * before minting, because an evicted localStorage is exactly the case
 * where a fresh token would split one person's history in two.
 */
export function reporterToken() {
    if (pending) return pending;
    pending = (async () => {
        const local = readLocal();
        if (local) {
            writePrefs(local);
            return local;
        }
        const token = (await readPrefs()) || mint();
        writeLocal(token);
        writePrefs(token);
        return token;
    })();
    return pending;
}

/**
 * Forget this install's token, so the next report or signup starts a
 * new one. Called on SIGN-OUT: the reports a device sends after its
 * owner signed out must not be claimable by whoever signs in on it
 * next. Returns the new token.
 */
export function resetReporterToken() {
    const token = mint();
    writeLocal(token);
    writePrefs(token);
    pending = Promise.resolve(token);
    return pending;
}
